/* Ход хвоста BotRun по сервису заказа (engine-kit, план движков §5.5 и §6 «Submit», «Tail actions», «Reconcile», «Done»).
   Правда — только в сервисе заказа (OrderService): водитель читает get()/derive()/outcome и переводит их на язык BotRun шаблона —
   adopt + show (показать с нуля, без перехода), advance (этап), finish (итог с переходом), exit (назад к форме переходом шаблона).
   Хвост рисует только то, что знает сервис: сам водитель ничего не отправляет при открытии и при сверке (StudioScreen.safety:35);
   отправка — только из drive (нажатие «Создать», «Проверить ещё раз» без заказа, «Попробовать ещё раз» на сбое).
   Этапы (решение владельца Q1): подписи — локальные этапы шаблона, текущий — тот локальный, чей ключ равен ключу текущего этапа
   сервера (tailSteps/tailCurrent); незнакомый ключ и заказ без этапов держат прежний. Отказ до создания (Q2) — страница rej
   «Заказ не создан» с «К настройкам»: она стоит, пока человек сам не уйдёт к форме, и сверка её не снимает. Повтор сохранённой
   заявки, остановленный до POST (сменился владелец), — не «не создан»: первый POST мог создать заказ, поэтому страница unk.
   Первая картинка без данных заказа (подсмотренная заявка, номер заказа без ответа сервера) — только показ: когда сервис узнал
   правду, она встаёт с нуля, без перехода, без анимации итога и без вибрации (как TAIL.show шаблона при открытии). */
import { readHistoryRepeat } from '../../../bridge/history-repeat';
import { tailCurrent, tailPhase, tailSteps } from '../circle/order-tail-model';
import { markSeen } from '../circle/tool-helpers';
import type { SpeechOrder } from '../../../api/speech';
import { restore, type Intent, type OrderDerived, type OrderService, type OrderSnapshot, type SubmitOutcome } from './order-service';
import type { BotRunApi, BotRunRun } from './bot-run-server';

export type FramePhase = 'load' | 'form' | 'run' | 'done' | 'mod' | 'tech' | 'uncertain' | 'unk';

/** Свой вид сбоя заказа экрана (отказ по входу, а не сбой сервиса: stt_no_speech — страница mod с «Заменить файл»); null — по заказу */
export type FailKind = (order: SpeechOrder) => 'mod' | 'tech' | null | undefined;

/** Что показывает кадр: владелец ещё не проверен — load; есть номер заказа — фаза хвоста по заказу (заказа ещё нет — run; сбой — mod или
    tech по заказу, а если экран судит сам — по failKind); заявка без номера (исход POST неизвестен) — unk; иначе форма */
export function phaseOf(s: OrderSnapshot, d: OrderDerived, failKind?: FailKind): FramePhase {
  if (!s.identity && !s.integrityError) return 'load';
  if (d.orderId) return (s.order?.status === 'failed' && failKind?.(s.order)) || tailPhase(s.order);
  if (s.intent) return 'unk';
  return 'form';
}

/** Текущий этап среди локальных этапов шаблона: ключ текущего этапа сервера (alias — ключ сервера → ключ шаблона, если они разные:
    у фотосессии look → check); сервер этапов не прислал или ключ незнакомый — held */
export function localStep(
  order: SpeechOrder | null | undefined,
  local: ReadonlyArray<readonly string[]>,
  held: number,
  alias?: Readonly<Record<string, string>>,
): number {
  const steps = tailSteps(order);
  if (!steps.length) return held;
  const key = steps[tailCurrent(order, steps)]?.key;
  const want = key !== undefined && alias && Object.hasOwn(alias, key) ? alias[key] : key;
  const at = local.findIndex(step => step[0] === want);
  return at >= 0 ? at : held;
}

type Want = { st: 'run' | 'done'; err: null } | { st: 'fail'; err: 'mod' | 'tech' | 'unk' };
/** Вид RUN BotRun для фазы хвоста; uncertain — unk («Проверить ещё раз» — только GET) */
function want(phase: Exclude<FramePhase, 'load' | 'form'>): Want {
  if (phase === 'run' || phase === 'done') return { st: phase, err: null };
  return { st: 'fail', err: phase === 'uncertain' ? 'unk' : phase };
}

/** Сохранённая заявка этого приложения для первой картинки, до проверки владельца сервисом: только чтение (владелец — из оболочки,
    window.BotServerMe), битая или чужая заявка — null. Решает потом сервис (owner): что он отверг, сверка уберёт.
    «Повторить» из «Истории» для этого приложения — тоже null: прежнюю заявку решает сервис (готовую снимает), а экран, как родная
    форма, открывается на форме в её загрузке, а не на странице заказа */
export function peekIntent(appId: number): Intent | null {
  const owner = (window as unknown as { BotServerMe?: { user?: { id?: unknown } } }).BotServerMe?.user?.id;
  if (typeof owner !== 'number' || !Number.isSafeInteger(owner) || owner <= 0) return null;
  if (readHistoryRepeat('studio', owner)?.app_id === appId) return null;
  try {
    return restore(owner, appId);
  } catch {
    return null;
  }
}

export interface RunDriverOptions {
  svc: OrderService;
  /** Хвост BotRun экрана (через фасад SV); читается при каждом обращении */
  tail(): BotRunApi;
  /** Контейнер экрана (там страница заказа или форма) */
  root(): HTMLElement | null;
  /** Экран открыт и кадр привязан: иначе водитель ничего не рисует (правду держит сервис, следующее открытие нарисует её) */
  mounted(): boolean;
  /** Локальные этапы шаблона по входу заказа: [ключ, значок, «идёт», «готово»] */
  localSteps(): string[][];
  /** Нарисовать форму экрана (paintStudio(pageHTML()) и formReady) */
  paintForm(): void;
  tip(el: Element | null, text: string): void;
  /** Заказ готов: вызывается при каждой сверке готового заказа (SV сам открывает результат один раз на id:revision:index) */
  onDone?(): void;
  /** Свой вид сбоя заказа (по умолчанию — по заказу: tailPhase) */
  failKind?: FailKind;
  /** Ключ этапа сервера → ключ локального этапа шаблона, где они разные */
  stepAlias?: Readonly<Record<string, string>>;
  /** Подшаг текущего этапа (k BotRun.advance: кадры, фото): обычно tailProgress(order)?.done; без него подшага нет */
  progress?(order: SpeechOrder | null): number | null | undefined;
}
export interface RunDriver {
  /** o.drive BotRun: «Создать» (TAIL.start), повтор заявки без заказа и «Попробовать ещё раз» на сбое (TAIL.retry) */
  drive(r: BotRunRun): Promise<void>;
  /** Сверка с сервисом: на каждом зафиксированном виде (EngineFrame → bound.changed()); пока идёт действие водителя — ничего */
  sync(): void;
  /** Первая картинка при открытии, только показ: состояние сервиса, а пока владелец не проверен — подсмотренная заявка */
  first(peek: Intent | null): 'form' | 'tail';
  /** «Проверить ещё раз» (unk, uncertain): с номером заказа — только GET; без номера — TAIL.retry() (тот же ключ) */
  check(btn: HTMLElement): Promise<void>;
  /** «Изменить», «К настройкам», «Заменить запись»: returnToForm, просмотрено и order-clear, затем go(); replace — и убрать запись key */
  leave(go: () => void, replace: boolean, key: string): Promise<void>;
  /** Идёт действие водителя: sync() не сверяет */
  busy(): boolean;
}

const orderClear = (id: number) => document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id } }));
const errorOf = (s: OrderSnapshot) => s.integrityError || s.error;
/** Что показывают тексты итога (подпись ошибки и чек): изменилось на том же итоге — страница перерисовывается на месте */
function pageKey(s: OrderSnapshot): string {
  const order = s.order as (SpeechOrder & { charge?: unknown; balance?: unknown; error?: unknown }) | null;
  return JSON.stringify([order?.id ?? null, order?.status ?? null, order?.financial ?? null, order?.charge ?? null, order?.balance ?? null, order?.error ?? null, errorOf(s)]);
}
/** Предел ожидания смены страницы (SteosUI.swap идёт 170 мс): дольше ждать нечего, даже если класс .leave снял не swap */
const SWAP_LIMIT_MS = 1000;
/** Смена страницы шаблона на контейнере закончилась: SteosUI.swap ставит .leave, а через 170 мс снимает его и рисует (start BotRun
    рисует html() по RUN). Пока она ждёт, RUN менять нельзя: exit обнулит RUN, и отрисовка start упадёт на RUN.st. Наблюдатель
    срабатывает после задачи таймера swap, то есть уже после её отрисовки */
function swapped(root: HTMLElement | null): Promise<void> {
  if (!root?.classList.contains('leave')) return Promise.resolve();
  return new Promise(resolve => {
    const finish = () => {
      observer.disconnect();
      clearTimeout(limit);
      resolve();
    };
    const observer = new MutationObserver(() => {
      if (!root.classList.contains('leave')) finish();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    const limit = setTimeout(finish, SWAP_LIMIT_MS);
  });
}
/** «Занята» кнопка шаблона (SteosUI.busy): крутилка на кнопке, пока идёт действие */
function busyButton<T>(btn: HTMLElement, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = (window as unknown as { SteosUI?: { busy?(btn: HTMLElement, fn: () => Promise<T>): Promise<T | undefined> } }).SteosUI?.busy;
  return busy ? busy(btn, fn) : fn();
}

export function createRunDriver(o: RunDriverOptions): RunDriver {
  const { svc } = o;
  let flight = 0;
  /** Ключ текстов, с которыми нарисован RUN */
  const painted = new WeakMap<BotRunRun, string>();
  /** RUN первой картинки, нарисованный без данных заказа (подсмотренная заявка; номер заказа без ответа сервера): первая сверка
      с данными ставит правду с нуля (adopt + show), а не ведёт его дальше как живой (finish с анимацией и вибрацией, exit со swap) */
  const held = new WeakSet<BotRunRun>();
  const live = () => {
    const s = svc.get();
    return { s, d: svc.derive(s) };
  };
  const current = (r: BotRunRun) => o.mounted() && o.tail().run === r;
  const footBtn = () => o.root()?.querySelector('.ap-foot .btn') ?? null;
  const tip = (el: Element | null, text: string) => {
    if (text) o.tip(el, text);
  };
  const done = (phase: FramePhase) => {
    if (phase === 'done') o.onDone?.();
  };
  const framePhase = (s: OrderSnapshot, d: OrderDerived) => phaseOf(s, d, o.failKind);
  const stepOf = (order: SpeechOrder | null, local: ReadonlyArray<readonly string[]>, held: number) => localStep(order, local, held, o.stepAlias);
  /** Подшаг этапа по заказу (o.progress); его нет — undefined, и BotRun ведёт этап без подшага */
  const partOf = (order: SpeechOrder | null) => {
    const k = o.progress?.(order);
    return typeof k === 'number' && Number.isFinite(k) ? k : undefined;
  };
  /** BotRun.advance: этап и подшаг (без подшага — как раньше, одним аргументом) */
  const advance = (T: BotRunApi, at: number, order: SpeechOrder | null) => {
    const k = partOf(order);
    if (k === undefined) T.advance(at);
    else T.advance(at, k);
  };

  /** Хвост с нуля по состоянию сервиса, без перехода: заказ после открытия, фоновая смена, восстановление после неудачи.
      peeked — фаза взята из подсмотренной заявки (сервис владельца ещё не проверил) */
  function show(phase: Exclude<FramePhase, 'load' | 'form'>, s: OrderSnapshot, peeked = false) {
    const T = o.tail(),
      local = o.localSteps(),
      w = want(phase);
    const k = w.st === 'run' ? partOf(s.order) : undefined;
    const run = T.adopt({
      st: w.st,
      err: w.err,
      step: w.st === 'done' ? local.length : stepOf(s.order, local, T.run?.step ?? 0),
      ...(k !== undefined ? { k } : {}),
    });
    painted.set(run, pageKey(s));
    if (peeked || (!!svc.derive(s).orderId && !s.order)) held.add(run);
    const root = o.root();
    if (root) T.show(root);
  }
  /** Заказа BotRun больше нет, без перехода и без отрисовки (drop закрывает только итог, поэтому RUN сначала становится итогом) */
  function forget(T: BotRunApi) {
    if (!T.run) return;
    T.adopt({ st: 'done', err: null });
    T.drop();
  }
  /** Нарисовать то, что говорит сервис: форма — переходом шаблона (exit идёт после ждущей смены start), хвост — с нуля */
  function rebuild() {
    const { s, d } = live();
    const phase = framePhase(s, d);
    const T = o.tail();
    if (phase === 'form' || phase === 'load') {
      if (T.run) T.exit(o.paintForm);
      else o.paintForm();
      return;
    }
    show(phase, s);
    done(phase);
  }
  function finish(r: BotRunRun, st: 'done' | 'fail', err: BotRunRun['err']) {
    o.tail().finish(st, err);
    painted.set(r, pageKey(svc.get()));
  }
  /** Что сделать после submit по его итогу (§6 «Submit», п. 5) */
  function after(r: BotRunRun, outcome: SubmitOutcome | null): () => void {
    if (outcome === 'created') return sync;
    if (outcome === 'dropped') return () => finish(r, 'fail', 'rej');
    /* остановлен до POST: «Заказ не создан» — только если сохранённой заявки без номера нет (первый «Создать»); повтор такой
       заявки мог создать заказ первым POST — страница unk («Заказ не подтверждён» и причина), заявка остаётся */
    if (outcome === 'blocked')
      return () => {
        const { s, d } = live();
        finish(r, 'fail', s.intent && !d.orderId ? 'unk' : 'rej');
      };
    if (outcome === 'kept') return () => finish(r, 'fail', 'unk');
    return rebuild;
  }
  async function submitFor(r: BotRunRun): Promise<(() => void) | null> {
    const { s, d } = live();
    const { actions } = svc;
    if (d.orderId && s.order?.status === 'failed') {
      /* «Попробовать ещё раз» со сбоя: форма с тем же входом, сбойный просмотрен, затем новый заказ с новым ключом; страница хода стоит.
         Скидка сбойного заказа («Сделать дешевле», если форма её публикует) остаётся: тот же вход — та же цена */
      const prev = d.orderId,
        cheap = !!s.intent?.body.cheap;
      if (!(await actions.returnToForm()))
        return () => {
          rebuild();
          tip(footBtn(), errorOf(svc.get()));
        };
      markSeen(prev);
      orderClear(prev);
      if (!current(r)) return null;
      await actions.submit(cheap ? { cheap } : {});
    } else if (s.intent && !d.orderId) await actions.submit({}); /* заявка без номера: тот же ключ и то же тело */
    else await actions.submit({ cheap: !!r.snap?.cheap });
    return after(r, svc.outcome);
  }
  async function drive(r: BotRunRun) {
    flight++;
    let next: (() => void) | null;
    try {
      next = await submitFor(r);
      /* быстрый ответ (отказ без запроса, отказ сервиса) пришёл, пока страница start ещё сменяется: следующий шаг — после её отрисовки */
      if (next) await swapped(o.root());
    } finally {
      flight--;
    }
    /* кадр закрыли посреди действия — ничего: правда в сервисе, следующее открытие нарисует её */
    if (next && current(r)) next();
  }

  /** Хвост, который закрывает переход самого leave (go: каталог или форма): sync не уводит его своим T.exit к форме, пока он гаснет */
  let leaving: BotRunRun | null = null;

  function sync() {
    if (flight || !o.mounted()) return;
    const { s, d } = live();
    const phase = framePhase(s, d);
    if (phase === 'load') return;
    const T = o.tail(),
      r = T.run;
    if (r && held.has(r)) {
      /* первая картинка без данных заказа: пока ответа о заказе нет — она стоит; пришёл — правда с нуля, без перехода */
      if (phase === 'run' && r.st === 'run' && d.orderId && !s.order) return;
      held.delete(r);
      if (phase === 'form') {
        forget(T);
        o.paintForm();
        return;
      }
      show(phase, s);
      done(phase);
      return;
    }
    if (phase === 'form') {
      /* заявку отверг сервис (снятая, «Изменить» в другом месте) — назад к форме; отказ до создания стоит до «К настройкам».
         Хвост, который закрывает сам leave («Другой шаблон», «Ещё вариант»), уводит его переход: второй T.exit к форме перекрыл бы каталог */
      if (r && r.err !== 'rej' && r !== leaving) T.exit(o.paintForm);
      return;
    }
    const w = want(phase);
    if (!r) show(phase, s);
    else if (r.st === 'run') {
      const at = stepOf(s.order, r.steps, r.step);
      if (w.st === 'run') advance(T, at, s.order);
      else {
        if (w.st === 'fail') advance(T, at, s.order);
        finish(r, w.st, w.err);
      }
    } else if (r.st !== w.st || r.err !== w.err) show(phase, s);
    else if (painted.get(r) !== pageKey(s)) {
      /* тот же итог, новые данные сервера (возврат Circle, статус uncertain): тексты и чек на месте */
      painted.set(r, pageKey(s));
      T.refresh();
    }
    done(phase);
  }

  function first(peek: Intent | null): 'form' | 'tail' {
    const { s, d } = live();
    let phase = framePhase(s, d);
    const peeked = phase === 'load' && !!peek;
    if (peeked) phase = peek.orderId ? 'run' : 'unk';
    if (phase === 'form' || phase === 'load') {
      forget(o.tail());
      o.paintForm();
      return 'form';
    }
    show(phase, s, peeked);
    return 'tail';
  }

  async function check(btn: HTMLElement) {
    if (flight) return;
    if (!live().d.orderId) {
      const T = o.tail();
      T.retry();
      /* повтор не начался (форма говорит, чего не хватает) — страница по сервису */
      if (!T.run) rebuild();
      return;
    }
    flight++;
    try {
      await busyButton(btn, () => svc.actions.refreshStatus());
    } finally {
      flight--;
    }
    if (!o.mounted()) return;
    const text = errorOf(svc.get());
    sync();
    /* сверка могла перерисовать подвал — подсказка у кнопки на месте */
    tip(btn.isConnected ? btn : footBtn(), text);
  }

  async function leave(go: () => void, replace: boolean, key: string) {
    if (flight) return;
    const { s, d } = live();
    const id = d.orderId;
    if (!id) {
      /* отказ до создания (rej): заказа нет, к форме сразу; цена или версия сменились — форма перечитывается */
      if (s.versionStale) svc.setters.revision(value => value + 1);
      if (replace) svc.actions.clearUpload(key);
      go();
      return;
    }
    flight++;
    let ok: true | undefined;
    try {
      ok = await svc.actions.returnToForm();
    } finally {
      flight--;
    }
    if (!ok) {
      if (o.mounted()) {
        rebuild();
        tip(footBtn(), errorOf(svc.get()));
      }
      return;
    }
    markSeen(id);
    orderClear(id);
    if (!o.mounted()) return;
    if (replace) svc.actions.clearUpload(key);
    leaving = o.tail().run;
    go();
  }

  return { drive, sync, first, check, leave, busy: () => flight > 0 };
}
